/* ------------------------------------------------------------------
   AdminPage.js — owner-only admin dashboard.
   Overview (users, plans, SES capacity, paused senders, volume) + a
   users table with inline plan editing and re-enable/pause sending, plus
   'Sign-ups by source' for the name-generator pages (T25).
   Access is enforced server-side (403 for non-admins).
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const ax = axios.create({ withCredentials: true });
const PLANS = ['free', 'starter', 'pro', 'agency'];

export default function AdminPage() {
  const [allowed, setAllowed] = useState(null);   // null=checking, false=denied, true=ok
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ax.get('/api/admin/users', { params: { search, plan: planFilter, flagged: flagged ? 1 : 0, page } });
      setUsers(data.users || []); setTotal(data.total || 0); setAllowed(true);
    } catch (e) {
      if (e.response?.status === 403) setAllowed(false);
      else console.error('admin users', e);
    } finally { setLoading(false); }
  }, [search, planFilter, flagged, page]);

  useEffect(() => {
    (async () => {
      try { const { data } = await ax.get('/api/admin/overview'); setOverview(data); setAllowed(true); }
      catch (e) { if (e.response?.status === 403) setAllowed(false); }
    })();
  }, []);
  useEffect(() => { const t = setTimeout(loadUsers, 200); return () => clearTimeout(t); }, [loadUsers]);

  async function changePlan(u, plan) {
    try { await ax.put(`/api/admin/users/${u.id}/plan`, { plan }); setUsers(us => us.map(x => x.id === u.id ? { ...x, plan } : x)); flash(`${u.email} → ${plan}`); }
    catch { flash('Plan change failed', false); }
  }
  async function toggleSending(u) {
    const enabled = u.email_sending_enabled === 0;   // currently paused → enable
    try { await ax.post(`/api/admin/users/${u.id}/sending`, { enabled }); setUsers(us => us.map(x => x.id === u.id ? { ...x, email_sending_enabled: enabled ? 1 : 0, email_paused_reason: enabled ? null : 'Paused by admin' } : x)); flash(enabled ? 'Sending re-enabled' : 'Sending paused'); }
    catch { flash('Failed', false); }
  }

  if (allowed === false) return <div style={{ padding: 40, color: '#6b7280' }}>You don’t have access to this page.</div>;

  const rate = (num, den) => den > 0 ? (num / den) * 100 : 0;
  const pct = (v) => `${v.toFixed(v < 1 && v > 0 ? 2 : 1)}%`;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Admin</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Users, plans, and email sending health.</p>
      </div>
      {msg && <Banner msg={msg} />}

      {/* overview */}
      {overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Stat label="Total users" value={overview.totalUsers?.toLocaleString()} sub={`+${overview.newUsers7d || 0} this week`} />
          <Stat label="Plans" value={PLANS.map(p => `${overview.byPlan?.[p] || 0} ${p[0].toUpperCase()}`).join(' · ')} small />
          <Stat label="Paused senders" value={overview.pausedCount || 0} danger={overview.pausedCount > 0} />
          <Stat label="Emails (24h)" value={(overview.emails24h || 0).toLocaleString()} sub={`${(overview.emails30d || 0).toLocaleString()} in 30d`} />
          {overview.sesQuota && (
            <Stat label="SES daily capacity"
              value={overview.sesQuota.max24 > 0 ? `${(overview.sesQuota.sent24 || 0).toLocaleString()} / ${overview.sesQuota.cap?.toLocaleString()}` : 'Unlimited'}
              sub={overview.sesQuota.max24 > 0 ? `cap = 90% of ${overview.sesQuota.max24.toLocaleString()}/day` : 'no daily limit'}
              danger={overview.sesQuota.cap && overview.sesQuota.sent24 >= overview.sesQuota.cap * 0.8} />
          )}
        </div>
      )}

      <SignupSources />

      {/* filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search name or email…" style={{ ...inputStyle, maxWidth: 280 }} />
        <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, width: 'auto' }}>
          <option value="">All plans</option>{PLANS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#374151', cursor: 'pointer' }}>
          <input type="checkbox" checked={flagged} onChange={e => { setFlagged(e.target.checked); setPage(1); }} /> Paused only
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>{total.toLocaleString()} users</span>
      </div>

      {/* users table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'auto' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : users.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No users match.</div>
          : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 820 }}>
            <thead><tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
              <Th>User</Th><Th>Plan</Th><Th>Sending</Th><Th>Sent 30d</Th><Th>Bounce</Th><Th>Complaint</Th><Th>Joined</Th>
            </tr></thead>
            <tbody>
              {users.map(u => {
                const bounce = rate(u.hardBounces30, u.deliveries30 + u.hardBounces30);
                const complaint = rate(u.complaints30, u.deliveries30);
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td><div style={{ fontWeight: 600, color: '#111827' }}>{u.name || '—'}</div><div style={{ color: '#6b7280', fontSize: 12.5 }}>{u.email}</div></Td>
                    <Td>
                      <select value={u.plan} onChange={e => changePlan(u, e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12.5 }}>
                        {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </Td>
                    <Td>
                      {u.email_sending_enabled === 0
                        ? <span title={u.email_paused_reason || ''} style={{ ...pill, background: '#fef2f2', color: '#dc2626' }}>Paused</span>
                        : <span style={{ ...pill, background: '#dcfce7', color: '#166534' }}>Active</span>}
                      <button onClick={() => toggleSending(u)} style={{ ...miniBtn, marginLeft: 8 }}>{u.email_sending_enabled === 0 ? 'Re-enable' : 'Pause'}</button>
                    </Td>
                    <Td>{(u.sent30 || 0).toLocaleString()}</Td>
                    <Td><span style={{ color: bounce >= 5 ? '#dc2626' : bounce >= 3 ? '#b45309' : '#6b7280', fontWeight: bounce >= 3 ? 700 : 400 }}>{pct(bounce)}</span></Td>
                    <Td><span style={{ color: complaint >= 0.3 ? '#dc2626' : complaint >= 0.1 ? '#b45309' : '#6b7280', fontWeight: complaint >= 0.1 ? 700 : 400 }}>{pct(complaint)}</span></Td>
                    <Td><span style={{ color: '#6b7280' }}>{String(u.created_at || '').slice(0, 10)}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ ...miniBtn, opacity: page <= 1 ? 0.5 : 1 }}>← Prev</button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {Math.ceil(total / 50)}</span>
          <button onClick={() => setPage(p => (p * 50 < total ? p + 1 : p))} disabled={page * 50 >= total} style={{ ...miniBtn, opacity: page * 50 >= total ? 0.5 : 1 }}>Next →</button>
        </div>
      )}
    </div>
  );
}

/* Sign-ups by source + the name-generator funnel (Growth round, T25).
   GET /api/admin/signup-sources?days=7|28 (backend/services/signupTracking.js). */
const FUNNEL_COLUMNS = [
  ['visits', 'Visits'], ['names_generated', 'Names generated'], ['claim_click', 'Claim clicks'],
  ['ai_vibe_used', 'AI vibe used'], ['kit_unlock_click', 'Kit unlocks'],
  ['signup_started', 'Sign-ups started'], ['signup_completed', 'Sign-ups finished'], ['paid', 'On a paid plan'],
];

export function SignupSources() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    ax.get('/api/admin/signup-sources', { params: { days } })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [days]);

  const setup = data?.setup || {};
  const n = v => Number(v || 0).toLocaleString();

  return (
    <section aria-labelledby="signup-sources-title" data-testid="signup-sources"
      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 id="signup-sources-title" style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Sign-ups by source</h2>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '2px 0 0' }}>The name-generator pages: where sign-ups came from, and each page’s funnel.</p>
        </div>
        <div role="group" aria-label="Period" style={{ display: 'flex', gap: 6 }}>
          {[7, 28].map(d => (
            <button key={d} type="button" onClick={() => setDays(d)} aria-pressed={days === d}
              style={{ ...miniBtn, ...(days === d ? { background: '#2563eb', border: '1px solid #2563eb', color: '#fff' } : {}) }}>
              Last {d} days
            </button>
          ))}
        </div>
      </div>

      {failed ? <div style={{ padding: 16, color: '#dc2626', fontSize: 13.5 }}>Couldn’t load sign-up sources.</div>
        : !data ? <div style={{ padding: 16, color: '#9ca3af', fontSize: 13.5 }}>Loading…</div>
        : (
        <>
          {(setup.signupColumns === false || setup.eventsTable === false) && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, fontSize: 13, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
              Not collecting everything yet: run <code>backend/sql/signup_tracking.sql</code> on the server
              {setup.signupColumns === false ? ' (sign-ups are not linked to a source)' : ''}
              {setup.eventsTable === false ? ' (page visits and clicks are not counted)' : ''}.
            </div>
          )}

          <div style={{ overflow: 'auto', border: '1px solid #f1f5f9', borderRadius: 8, marginBottom: 14 }}>
            <table aria-label="Funnel by page" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 820 }}>
              <thead><tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Page</Th>{FUNNEL_COLUMNS.map(([k, label]) => <Th key={k}>{label}</Th>)}
              </tr></thead>
              <tbody>
                {data.funnel?.length ? data.funnel.map(r => (
                  <tr key={r.page} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td><span style={{ fontWeight: 600 }}>{r.page}</span></Td>
                    {FUNNEL_COLUMNS.map(([k]) => <Td key={k}>{n(r[k])}</Td>)}
                  </tr>
                )) : <tr><td colSpan={FUNNEL_COLUMNS.length + 1} style={{ padding: 16, textAlign: 'center', color: '#9ca3af' }}>No visits in the last {data.days} days.</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={{ overflow: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 }}>
            <table aria-label="Sign-ups by source" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 520 }}>
              <thead><tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Source</Th><Th>First page</Th><Th>Sign-ups</Th><Th>On a paid plan</Th>
              </tr></thead>
              <tbody>
                {data.sources?.length ? data.sources.map(s => (
                  <tr key={`${s.source}|${s.page}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td>{s.source}</Td><Td>{s.page}</Td><Td>{n(s.signups)}</Td><Td>{n(s.paid)}</Td>
                  </tr>
                )) : <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#9ca3af' }}>No sign-ups from the name generators in the last {data.days} days.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

const Stat = ({ label, value, sub, danger, small }) => (
  <div style={{ background: '#fff', border: `1px solid ${danger ? '#fecaca' : '#e5e7eb'}`, borderRadius: 12, padding: '14px 16px' }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: small ? 14 : 22, fontWeight: 700, color: danger ? '#dc2626' : '#111827' }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
  </div>
);
const inputStyle = { width: '100%', padding: '8px 11px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const miniBtn = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const pill = { fontSize: 11.5, fontWeight: 700, padding: '2px 9px', borderRadius: 99 };
const Th = ({ children }) => <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '10px 14px', color: '#111827', verticalAlign: 'middle' }}>{children}</td>;
const Banner = ({ msg }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626', border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
);
